import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  DatePicker,
  Progress,
  Tag,
  Tabs,
  Spin,
  Empty,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { getWastageAnalytics, getWastageTrend } from "../../api/wastageApi";

const { RangePicker } = DatePicker;

const WastageAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs(),
  ]);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, trendRes] = await Promise.all([
        getWastageAnalytics({
          start_date: dateRange[0].format("YYYY-MM-DD"),
          end_date: dateRange[1].format("YYYY-MM-DD"),
        }),
        getWastageTrend({
          days: dateRange[1].diff(dateRange[0], "day") + 1,
        }),
      ]);
      setAnalytics(analyticsRes);
      setTrendData(Array.isArray(trendRes) ? trendRes : []);
    } catch (error) {
      toast.error("Failed to load wastage analytics");
    }
    setLoading(false);
  };

  // ── Derived data from analytics response ───────────────────

  const totals = analytics?.totals || {};
  const totalWastage = totals._sum?.wastage_quantity || 0;
  const totalCost = totals._sum?.material_cost || 0;
  const avgPct = totals._avg?.wastage_pct || 0;
  const entryCount = totals._count || 0;

  const byStage = analytics?.by_stage || [];
  const byReason = analytics?.by_reason || [];
  const byMachine = analytics?.by_machine || [];

  // ── Stage table ────────────────────────────────────────────

  const stageColumns = [
    {
      title: "Stage",
      dataIndex: "stage_id",
      key: "stage",
      render: (_, record) => record.stage?.name || record.stage_id || "-",
    },
    {
      title: "Entries",
      dataIndex: "_count",
      key: "count",
    },
    {
      title: "Total Wastage",
      key: "wastage",
      render: (_, record) =>
        (record._sum?.wastage_quantity || 0).toLocaleString(),
    },
    {
      title: "Cost (₹)",
      key: "cost",
      render: (_, record) =>
        `₹${(record._sum?.material_cost || 0).toLocaleString()}`,
    },
    {
      title: "Share",
      key: "share",
      render: (_, record) => {
        const qty = record._sum?.wastage_quantity || 0;
        const pct =
          totalWastage > 0 ? Math.round((qty / totalWastage) * 100) : 0;
        return (
          <Progress
            percent={pct}
            size="small"
            status={pct > 30 ? "exception" : "normal"}
          />
        );
      },
    },
  ];

  // ── Reason table ───────────────────────────────────────────

  const reasonColumns = [
    {
      title: "Reason",
      dataIndex: "reason_category",
      key: "reason",
      render: (v) => (
        <Tag
          color={
            v === "machine_error"
              ? "red"
              : v === "operator_error"
              ? "orange"
              : v === "material_defect"
              ? "purple"
              : v === "setup_waste"
              ? "blue"
              : "default"
          }
        >
          {v?.replace(/_/g, " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Entries",
      dataIndex: "_count",
      key: "count",
    },
    {
      title: "Total Wastage",
      key: "wastage",
      render: (_, record) =>
        (record._sum?.wastage_quantity || 0).toLocaleString(),
    },
    {
      title: "Cost (₹)",
      key: "cost",
      render: (_, record) =>
        `₹${(record._sum?.material_cost || 0).toLocaleString()}`,
    },
    {
      title: "Share",
      key: "share",
      render: (_, record) => {
        const qty = record._sum?.wastage_quantity || 0;
        const pct =
          totalWastage > 0 ? Math.round((qty / totalWastage) * 100) : 0;
        return (
          <Progress
            percent={pct}
            size="small"
            status={pct > 25 ? "exception" : "normal"}
          />
        );
      },
    },
  ];

  // ── Machine table ──────────────────────────────────────────

  const machineColumns = [
    {
      title: "Machine",
      key: "machine",
      render: (_, record) =>
        record.machine?.name || record.machine_id || "-",
    },
    {
      title: "Entries",
      dataIndex: "_count",
      key: "count",
    },
    {
      title: "Total Wastage",
      key: "wastage",
      render: (_, record) =>
        (record._sum?.wastage_quantity || 0).toLocaleString(),
    },
    {
      title: "Cost (₹)",
      key: "cost",
      render: (_, record) =>
        `₹${(record._sum?.material_cost || 0).toLocaleString()}`,
    },
  ];

  // ── Trend table ────────────────────────────────────────────

  const trendColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (d) => dayjs(d).format("DD MMM"),
    },
    {
      title: "Wastage Qty",
      dataIndex: "total_wastage",
      key: "wastage",
      render: (v) => (v || 0).toLocaleString(),
    },
    {
      title: "Entries",
      dataIndex: "count",
      key: "count",
    },
    {
      title: "Avg %",
      dataIndex: "avg_pct",
      key: "avg_pct",
      render: (v) => (
        <span
          style={{ color: v > 3 ? "#ff4d4f" : v > 2 ? "#fa8c16" : "#52c41a" }}
        >
          {v?.toFixed(1)}%
        </span>
      ),
    },
    {
      title: "Visual",
      key: "bar",
      render: (_, record) => {
        const maxWaste = Math.max(
          ...trendData.map((t) => t.total_wastage || 0),
          1
        );
        const pct = Math.round(
          ((record.total_wastage || 0) / maxWaste) * 100
        );
        return (
          <div
            style={{
              width: `${pct}%`,
              minWidth: 4,
              height: 16,
              backgroundColor:
                record.avg_pct > 3
                  ? "#ff4d4f"
                  : record.avg_pct > 2
                  ? "#fa8c16"
                  : "#52c41a",
              borderRadius: 3,
            }}
          />
        );
      },
    },
  ];

  return (
    <Card
      title="Wastage Analytics"
      extra={
        <RangePicker
          value={dateRange}
          onChange={(val) => val && setDateRange(val)}
          format="DD MMM YYYY"
        />
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : !analytics ? (
        <Empty description="No data available" />
      ) : (
        <>
          {/* Summary Stats */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Total Wastage Cost"
                  value={totalCost}
                  prefix="₹"
                  valueStyle={{ color: "#cf1322" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Avg Wastage %"
                  value={avgPct}
                  precision={2}
                  suffix="%"
                  valueStyle={{
                    color: avgPct > 3 ? "#cf1322" : "#3f8600",
                  }}
                  prefix={
                    avgPct > 3 ? (
                      <ArrowUpOutlined />
                    ) : (
                      <ArrowDownOutlined />
                    )
                  }
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic title="Total Entries" value={entryCount} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Total Qty Wasted"
                  value={totalWastage}
                  valueStyle={{ color: "#cf1322" }}
                />
              </Card>
            </Col>
          </Row>

          <Tabs
            items={[
              {
                key: "by-stage",
                label: "By Stage",
                children: (
                  <Table
                    columns={stageColumns}
                    dataSource={byStage}
                    rowKey={(r) => r.stage_id || Math.random()}
                    pagination={false}
                    size="small"
                  />
                ),
              },
              {
                key: "by-reason",
                label: "By Reason",
                children: (
                  <Table
                    columns={reasonColumns}
                    dataSource={byReason}
                    rowKey={(r) => r.reason_category || Math.random()}
                    pagination={false}
                    size="small"
                  />
                ),
              },
              {
                key: "by-machine",
                label: "By Machine",
                children: (
                  <Table
                    columns={machineColumns}
                    dataSource={byMachine}
                    rowKey={(r) => r.machine_id || Math.random()}
                    pagination={false}
                    size="small"
                  />
                ),
              },
              {
                key: "trend",
                label: "Daily Trend",
                children:
                  trendData.length > 0 ? (
                    <Table
                      columns={trendColumns}
                      dataSource={trendData}
                      rowKey="date"
                      pagination={
                        trendData.length > 15
                          ? { pageSize: 15 }
                          : false
                      }
                      size="small"
                    />
                  ) : (
                    <Empty description="No trend data available" />
                  ),
              },
            ]}
          />

          {/* Alerts */}
          {avgPct > 3 && (
            <Card
              size="small"
              title={
                <span>
                  <WarningOutlined
                    style={{ color: "#faad14", marginRight: 8 }}
                  />
                  High Wastage Alert
                </span>
              }
              style={{ marginTop: 16 }}
            >
              <Tag color="red">
                Average wastage ({avgPct.toFixed(1)}%) exceeds 3% threshold
              </Tag>
              {byStage
                .filter(
                  (s) =>
                    s._sum?.wastage_quantity > 0 &&
                    totalWastage > 0 &&
                    (s._sum.wastage_quantity / totalWastage) * 100 > 30
                )
                .map((s) => (
                  <Tag key={s.stage_id} color="orange">
                    {s.stage?.name || s.stage_id}: High contributor
                  </Tag>
                ))}
            </Card>
          )}
        </>
      )}
    </Card>
  );
};

export default WastageAnalytics;
